import React, { useState, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { IndexPage } from "@/pages/IndexPage";
import { AboutPage } from "@/pages/AboutPage";
import { PricingPage } from "@/pages/PricingPage";
import { ServiceCommitmentsPage } from "@/pages/ServiceCommitmentsPage";
import { PrivacyPolicyPage } from "@/pages/PrivacyPolicyPage";
import { TermsOfServicePage } from "@/pages/TermsOfServicePage";
import { FairHousingPage } from "@/pages/FairHousingPage";

import { TestimonialsPage } from "@/pages/TestimonialsPage";
import { BlogPage } from "@/pages/BlogPage";
import { BlogPostPage } from "@/pages/BlogPostPage";
import { ContactPage } from "@/pages/ContactPage";
import { ServiceDetailPage } from "@/pages/ServiceDetailPage";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { LeadConsultationForm } from "@/components/LeadConsultationForm";
import NotFound from "@/pages/NotFound";

// Code-split below-the-fold / heavy components so they don't block initial paint.
const RentVsSellCalculator = lazy(() =>
  import("@/components/RentVsSellCalculator").then((m) => ({
    default: m.RentVsSellCalculator,
  })),
);
const VacancyLossCalculator = lazy(() =>
  import("@/components/VacancyLossCalculator").then((m) => ({
    default: m.VacancyLossCalculator,
  })),
);
const RoiCalculator = lazy(() =>
  import("@/components/RoiCalculator").then((m) => ({
    default: m.RoiCalculator,
  })),
);

const queryClient = new QueryClient();

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default function App() {
  const [consultationOpen, setConsultationOpen] = useState(false);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ScrollToTop />
        <div className="min-h-screen flex flex-col font-sans bg-background text-foreground antialiased selection:bg-accent/20">
          <Navbar onOpenConsultation={() => setConsultationOpen(true)} />

          <main className="flex-grow">
            <Routes>
              <Route
                path="/"
                element={
                  <IndexPage
                    onOpenConsultation={() => setConsultationOpen(true)}
                  />
                }
              />
              <Route path="/about" element={<AboutPage />} />
              <Route
                path="/pricing"
                element={
                  <PricingPage
                    onOpenConsultation={() => setConsultationOpen(true)}
                  />
                }
              />
              <Route
                path="/guarantees"
                element={
                  <ServiceCommitmentsPage
                    onOpenConsultation={() => setConsultationOpen(true)}
                  />
                }
              />

              <Route path="/testimonials" element={<TestimonialsPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route
                path="/blog/:slug"
                element={
                  <BlogPostPage
                    onOpenConsultation={() => setConsultationOpen(true)}
                  />
                }
              />
              <Route path="/contact" element={<ContactPage />} />

              {/* Legal Pages */}
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route
                path="/terms-of-service"
                element={<TermsOfServicePage />}
              />
              <Route path="/fair-housing" element={<FairHousingPage />} />

              {/* Service Pages */}
              <Route
                path="/property-management"
                element={
                  <ServiceDetailPage
                    serviceKey="property-management"
                    onOpenConsultation={() => setConsultationOpen(true)}
                  />
                }
              />
              <Route
                path="/marketing"
                element={
                  <ServiceDetailPage
                    serviceKey="marketing"
                    onOpenConsultation={() => setConsultationOpen(true)}
                  />
                }
              />
              <Route
                path="/tenant-screening"
                element={
                  <ServiceDetailPage
                    serviceKey="tenant-screening"
                    onOpenConsultation={() => setConsultationOpen(true)}
                  />
                }
              />
              <Route
                path="/rent-collection"
                element={
                  <ServiceDetailPage
                    serviceKey="rent-collection"
                    onOpenConsultation={() => setConsultationOpen(true)}
                  />
                }
              />
              <Route
                path="/maintenance-services"
                element={
                  <ServiceDetailPage
                    serviceKey="maintenance-services"
                    onOpenConsultation={() => setConsultationOpen(true)}
                  />
                }
              />
              <Route
                path="/accounting"
                element={
                  <ServiceDetailPage
                    serviceKey="accounting"
                    onOpenConsultation={() => setConsultationOpen(true)}
                  />
                }
              />
              <Route
                path="/eviction"
                element={
                  <ServiceDetailPage
                    serviceKey="eviction"
                    onOpenConsultation={() => setConsultationOpen(true)}
                  />
                }
              />

              {/* Calculator Pages */}
              <Route
                path="/rent-sell-calculator"
                element={
                  <div className="pt-28 pb-20 max-w-7xl mx-auto px-4">
                    <Suspense fallback={<div className="h-96" />}>
                      <RentVsSellCalculator />
                    </Suspense>
                  </div>
                }
              />
              <Route
                path="/vacancy-loss-calculator"
                element={
                  <div className="pt-28 pb-20 max-w-4xl mx-auto px-4">
                    <Suspense fallback={<div className="h-96" />}>
                      <VacancyLossCalculator />
                    </Suspense>
                  </div>
                }
              />
              <Route
                path="/roi-calculator"
                element={
                  <div className="pt-28 pb-20 max-w-4xl mx-auto px-4">
                    <Suspense fallback={<div className="h-96" />}>
                      <RoiCalculator />
                    </Suspense>
                  </div>
                }
              />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>

          <Footer />

          {/* Modal Consultation Form */}
          <Dialog open={consultationOpen} onOpenChange={setConsultationOpen}>
            <DialogContent className="max-w-xl p-0 border-0 bg-transparent overflow-hidden">
              <LeadConsultationForm
                compact
                onSuccess={() => {
                  setTimeout(() => setConsultationOpen(false), 2500);
                }}
              />
            </DialogContent>
          </Dialog>
        </div>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
